import MarketContextAPI from '@/api/market-context.api.js'

// POPULATION, first, because everything below inherits it: as of 2026-09-04 the winning-bid
// distribution these tiers are scored against is measured ONLY over auctions a call actually
// arrived on. Winning an auction and receiving a call are different events - a campaign can
// win an auction the seller never routed a call into - and the exchange now counts only the
// second kind.
//
// ⚠ THE USER-FACING VOCABULARY DID NOT CHANGE WITH IT, and that is an owner decision, not an
// oversight. On this UI a WINNING BID MEANS A DELIVERED CALL - that is the term's definition
// here. So every string below still says "winning bid" / "winning bids", and a future change
// must not rename them to "delivered calls" or "calls that came through": that would create a
// second vocabulary for one concept and strand the buyers, docs and support macros that
// already speak the first. What narrowed is the measurement; what it is called is unchanged.
//
// rankBands and marketActivityLevels below are a genuinely DIFFERENT population - the ungated
// bidder census, because a competitor's posted price is real whether or not the seller
// delivered - and their "of bids" wording distinguishes them from the winning-bid strings
// above. See docs/MARKET_CONTEXT_DELIVERED_CALLS.md.
//
// Bid tiers classify a campaign's POSITIONED bid - its configured max bid for a Static
// campaign, its median observed bid for an RTB or Programmatic one, which prices every ping
// itself - against the winning-bid distribution in its category + country over the market
// window:
//   Leading      - at or above P75
//   Competitive  - between P50 and P75
//   BelowMarket  - between P25 and P50
//   PricedOut    - below P25
//
// That window is NOT a fixed 24 hours. The endpoint reads a trailing 24 hours on six days of
// the week and a trailing 7 days on Mondays, because a Monday 24h window looks back at
// Sunday and this marketplace's weekend volume dip makes the percentiles above swing on a
// handful of auctions. It reports the length it used as windowHours, and the UI labels every
// number with it - see formatMarketWindow below and docs/MARKET_CONTEXT_MONDAY_WINDOW.md.
// The direct consequence: the same campaign, with the same bid, can carry a different tier
// on Monday than on Tuesday.
export const bidTiers = {
  Leading: 'Leading',
  Competitive: 'Competitive',
  BelowMarket: 'BelowMarket',
  PricedOut: 'PricedOut'
}

export function getBadgeVariantForBidTier(tier) {
  switch (tier) {
  case bidTiers.Leading:
    return 'success'
  case bidTiers.Competitive:
    return 'info'
  case bidTiers.BelowMarket:
    return 'warning'
  case bidTiers.PricedOut:
    return 'danger'
  default:
    return 'secondary'
  }
}

export function getBidTierText(tier) {
  switch (tier) {
  case bidTiers.Leading:
    return 'Leading'
  case bidTiers.Competitive:
    return 'Competitive'
  case bidTiers.BelowMarket:
    return 'Below Market'
  case bidTiers.PricedOut:
    return 'Priced Out'
  default:
    return ''
  }
}

// Rank bands are E1's answer to "where do I sit among this cell's buyers", and they are
// scored on the UNGATED bidder list - every buyer with a bid in the cell, whether or not a
// call came through on the auctions they won. That is why three of the four strings below say
// "of bids" rather than "of winning bids": ALL bids, won or lost, delivered or not, which is
// a strictly wider population than the winning-bid strings elsewhere describe. The endpoint
// deliberately ships a BAND rather than a rank or a buyer count - a count, even a fuzzed
// one, tells a buyer how many competitors they face, and a raw rank alone still implies
// cohort size ("#9" means at least nine buyers). The strings are response contract,
// rendered via getRankBandText below; null exactly when the row has no positioned bid,
// the same null-contract as tier.
export const rankBands = {
  TopBidder: 'TopBidder',
  Top25Percent: 'Top25Percent',
  UpperHalf: 'UpperHalf',
  LowerHalf: 'LowerHalf'
}

export function getRankBandText(band) {
  switch (band) {
  case rankBands.TopBidder:
    return 'Top bidder'
  case rankBands.Top25Percent:
    return 'Top 25% of bids'
  case rankBands.UpperHalf:
    return 'Top 50% of bids'
  case rankBands.LowerHalf:
    return 'Lower 50% of bids'
  default:
    return ''
  }
}

// E2's counterpart: how contested the category is over the last 7 days, as a qualitative
// label instead of a buyer count, for the same never-disclose-the-census reason as
// rankBands. Also UNGATED, like rankBands: a buyer bidding here is competition whether or
// not the seller sent a call into the auction they won, so this label answers "how busy" and
// never "how many calls got through". Null (an empty market cell) renders as empty and the
// panel hides the line - and note the panel hides ENTIRELY when there are no delivered calls,
// so a busy cell with nothing delivered never gets to show a "Highly competitive market"
// label with no prices beside it.
export const marketActivityLevels = {
  Quiet: 'Quiet',
  Active: 'Active',
  HighlyCompetitive: 'HighlyCompetitive'
}

export function getMarketActivityText(activity) {
  switch (activity) {
  case marketActivityLevels.Quiet:
    return 'Quiet market'
  case marketActivityLevels.Active:
    return 'Active market'
  case marketActivityLevels.HighlyCompetitive:
    return 'Highly competitive market'
  default:
    return ''
  }
}

// Share of last-7-day winning bids a given bid would have beaten, read as a step lookup
// off the endpoint's winRateCurve ECDF — the SAME curve the backend evaluates the tier
// cards' win rates from, so the two numbers can never disagree on one panel.
//
// Since 2026-09-04 the curve counts only auctions a call arrived on, so the denominator here
// is narrower than it used to be — the same number of winning bids the rest of this panel
// describes, since on this UI a winning bid means a delivered call. The wording stays
// "beaten ... of winning bids" for that reason. Contract:
// bidLevels sit on a CENT grid (each level is the winning bid rounded to the cent, half away
// from zero, in dollars: a $40.5059 winner sits at 40.51, so a typed $40.50 does not beat it),
// and both sides are compared in cents: a level as Math.round(level * 100), the bid as
// bid * 100 rounded to 4 decimal places, which removes float noise (19.99 * 100 is
// 1998.9999999999998) without snapping a sub-cent bid onto a cent. The compare is
// INCLUSIVE, so a tie with a winning bid counts as beating it. The C# helper
// (BuyerAnalyticsService.WinRateAt) does exactly the same, and Math.round on the share
// rounds half up like its MidpointRounding.AwayFromZero. A drift here shows up as a
// disagreement at exactly the tier bids. (Until 2026-10 the levels were whole-dollar
// ceil(winning bid) and the bid was read as Math.ceil(bid).)
export function estimateBeatsPercent(bid, categoryMarketContext) {
  const curve = categoryMarketContext?.winRateCurve

  if (!bid || !curve || !curve.bidLevels?.length) {
    return null
  }

  const bidCents = Math.round(bid * 100 * 10000) / 10000

  // Last level <= the bid, in cents; levels are ascending
  let lastIndex = -1
  for (let i = 0; i < curve.bidLevels.length && toCents(curve.bidLevels[i]) <= bidCents; i++) {
    lastIndex = i
  }

  if (lastIndex === -1) {
    return 0
  }

  return Math.round(curve.cumulativeShares[lastIndex] * 100)
}

// Two bids are "the same bid" when they render as the same currency string, so equality tests
// on bids run on cents rather than on raw dollars
export function toCents(bid) {
  return Math.round(bid * 100)
}

// Renders the market window the E1 rows were measured over, in the two lengths the UI needs:
// `short` for a column header, `long` for a sentence. The single source of this label - there
// is no other data-driven time-window string in the app and no duration helper in src/utils -
// so both live here beside getBidTierText, which the same components already import.
//
// An unknown window (the fetch has not resolved) deliberately renders as EMPTY STRINGS rather
// than defaulting to 24h. Defaulting would render `Missed (24h)` and then flip to
// `Missed (7d)` a moment later - a visible flash, on Mondays, on the exact label added to
// stop people misreading these numbers. Callers render the label bare in that case.
export function formatMarketWindow(hours) {
  if (!hours) {
    return { short: '', long: '' }
  }

  return hours >= 168
    ? { short: '7d', long: 'last 7 days' }
    : { short: '24h', long: 'last 24 hours' }
}

// ---------------------------------------------------------------------------
// Column-header and rank-line tooltip copy.
//
// These live here, beside getBidTierText and formatMarketWindow, rather than inline in the
// two components that render them, because the copy is the part most likely to be revised:
// getRankBandTooltip below is the UI half of an OPEN design decision (option (a) in
// docs/MARKET_CONTEXT_WIRING.md, "Tier vs rank band"), and if the band is ever rederived
// from the winning-price list instead of the buyer list, this string is what has to change
// - not the markup in ListBuyerCampaigns and BidOpportunityCell.
//
// All three take the formatMarketWindow result and drop the window phrase when it is empty,
// the same way missedCallsTooltip does: naming a window before the fetch resolves would
// print "last 24 hours" and then correct itself to "last 7 days" on a Monday.
// ---------------------------------------------------------------------------

export function getMarketPositionTooltip(marketWindow = {}) {
  const windowPhrase = marketWindow.long ? ` over the ${marketWindow.long}` : ''

  // No Static-vs-dynamic clause: the indicator's own line already labels the marker
  // `Your bid` or `Your typical bid` depending on routing type.
  //
  // This is one of the PAIR of column headers that names the two populations apart - this one
  // the prices auctions sold for, the Opportunity one other buyers' typical bids. That
  // pairing is what lets a row read "Leading" beside "Lower 50% of bids" without either
  // number being wrong, so the two must never drift onto the same phrasing. Note "auctions
  // actually sold for" now means auctions a call arrived on, which is the narrower set the
  // whole panel is scored against - the phrase was left alone because on this UI that IS what
  // a sold auction means.
  return 'Where this campaign\'s bid sits among the prices auctions actually sold for in this'
    + ` market${windowPhrase}. The bar spans the middle half of those prices; the marker is the bid`
    + ' it was scored on.'
}

// Deliberately about the RANK BAND only, not the raise chip that shares this column. The chip
// already reads as its own sentence (`+$4 -> est. +3 calls/day`); the band is the half nobody
// can decode from the cell text, so the header explains that and nothing else. It names the
// population and the ladder; the caveat that the band can disagree with the Market Position
// badge sits on the rank line itself - see getRankBandTooltip below.
//
// Window-aware because the band genuinely moves with the window: the market cell counts any
// buyer with a single bid in it, so the Monday 7-day window picks up occasional bidders and
// can demote a campaign a band with nobody having changed anything.
export function getOpportunityTooltip(marketWindow = {}) {
  const windowPhrase = marketWindow.long ? ` over the ${marketWindow.long}` : ''

  return `Where this campaign's bid ranks against other buyers' typical bids${windowPhrase}:`
    + ' top bidder, top 25%, top 50%, or lower 50%.'
}

// Names the comparison the band makes, and stops there. It deliberately does NOT spell out
// the contrast with the tier badge one column over, even though that is where the confusion
// lives: the two are computed from different populations - the band from one median bid per
// buyer in the market cell (ungated), the badge from one winning price per DELIVERED auction
// - so a row can legitimately read "Leading" beside "Lower 50% of bids" with both numbers
// exact. Since 2026-09-04 the badge's population shrank while the band's did not, so the
// badge's claim now rests on strictly fewer data points than the band's does. What
// carries that now is the PAIR of column headers, which name the two populations apart
// (getMarketPositionTooltip: prices auctions sold for; getOpportunityTooltip: other buyers'
// typical bids, delivered or not). Nothing states outright that the two can differ - a deliberate call, since
// the alternative was a sentence about a neighbouring column inside a hover on this one.
// Full derivation, worked example and the alternatives to labelling it at all:
// docs/MARKET_CONTEXT_WIRING.md, "Tier vs rank band".
export function getRankBandTooltip() {
  return 'Compares this campaign\'s bid with other buyers\' typical bids in this market.'
}

// Each fetch takes a ticket, and only the newest one may commit. Responses can arrive out of
// order - the queries run on ClickHouse and their latency varies with the size of the market
// cell - and the last response to arrive is not necessarily the one for what is on screen now.
let latestCampaignMarketPositionsRequest = 0
let latestCategoryMarketContextRequest = 0

const state = {
  // campaignId -> market position row, see MarketContextAPI.getCampaignMarketPositions
  campaignMarketPositions: {},
  // Length in hours of the window those rows were measured over: 24 or 168, straight off the
  // response envelope. Null until the first fetch resolves, which is what keeps the labels
  // bare rather than briefly wrong
  campaignMarketPositionsWindowHours: null,
  // The envelope's row cap was hit (more than `limit` market cells or bidding campaigns), so some
  // campaigns are missing from the rows or carry zeroed counts. They would otherwise render
  // exactly like a market with no auctions, and the missed-calls total would quietly undercount.
  campaignMarketPositionsTruncated: false,
  campaignMarketPositionsLimit: null,
  categoryMarketContext: null
}

const getters = {
  getMarketPositionForCampaign: (state) => (campaignId) => {
    return state.campaignMarketPositions[campaignId] || null
  },

  campaignMarketPositionsWindowHours: (state) => state.campaignMarketPositionsWindowHours,

  campaignMarketPositionsTruncated: (state) => state.campaignMarketPositionsTruncated,

  campaignMarketPositionsLimit: (state) => state.campaignMarketPositionsLimit,

  // Whether the signed-in user is shown market data at all. A softphone agent is not: they take
  // calls and do not set the account's bids, so the list's market columns and banner and the
  // budget step's market panel are hidden from them, and nothing here is even fetched for them.
  // Staff impersonating the account keep it, as they keep every column. The kill switch off makes
  // an agent an ordinary buyer, who sees it again. Applied in ListBuyerCampaigns and Budget.
  //
  // A UI rule only - the endpoints still answer an agent's token. See Product Requirements #5 in
  // docs/MARKET_CONTEXT_WIRING.md.
  canViewMarketContext: (state, getters, rootState, rootGetters) =>
    rootGetters['globalSettings/isSoftphoneUser'] !== true || rootGetters['auth/isImpersonating'] === true
}

const actions = {
  async fetchCampaignMarketPositions({ commit, rootGetters }) {
    const request = ++latestCampaignMarketPositionsRequest

    let response
    try {
      response = await MarketContextAPI.getCampaignMarketPositions(
        rootGetters['auth/accountDetails']?.id
      )
    } catch (error) {
      // A newer fetch is already on its way, and that one decides what the buyer hears
      if (request !== latestCampaignMarketPositionsRequest) return
      throw error
    }

    // An inline bid edit refetches, so two of these can overlap; an older response landing last
    // would put back the tier of a bid the campaign no longer has
    if (request !== latestCampaignMarketPositionsRequest) return

    // {rows, truncated, limit, windowHours} envelope; the endpoint returns a row for every
    // campaign itself, whatever its routing type, so there is no campaignIds param anymore.
    // Rows and window go in on ONE commit so no render can ever pair rows from one window
    // with another window's label
    commit('SET_CAMPAIGN_MARKET_POSITIONS', {
      rows: response.data.rows,
      windowHours: response.data.windowHours,
      truncated: response.data.truncated,
      limit: response.data.limit
    })
  },

  clearCategoryMarketContext({ commit }) {
    // Also voids any fetch still in flight, which would otherwise land on the cleared panel
    latestCategoryMarketContextRequest++
    commit('SET_CATEGORY_MARKET_CONTEXT', null)
  },

  async fetchCategoryMarketContext({ commit, rootGetters }, { categoryId, countryId, languageId }) {
    const request = ++latestCategoryMarketContextRequest

    // Clear first so a failed fetch never leaves the previous category's numbers on screen
    // under the new category's name. Clearing alone does not cover a slow fetch: that is the
    // ticket check below.
    commit('SET_CATEGORY_MARKET_CONTEXT', null)

    let response
    try {
      response = await MarketContextAPI.getCategoryMarketContext(
        rootGetters['auth/accountDetails']?.id,
        { categoryId, countryId, languageId }
      )
    } catch (error) {
      // The buyer has already moved off this market, so its failure is not theirs to hear about
      if (request !== latestCategoryMarketContextRequest) return
      throw error
    }

    // The buyer changed category, country or language (or blanked one) while this was in
    // flight. Committing it would show the old market's prices, and its "Use $X" bids, under
    // the new category's name.
    if (request !== latestCategoryMarketContextRequest) return

    // An empty market cell comes back with null percentiles (tiers: [],
    // winRateCurve: null) - commit null so the panel's v-if keeps it hidden
    const isEmptyMarket = response.data?.winningBidP25InUSD == null
    commit('SET_CATEGORY_MARKET_CONTEXT', isEmptyMarket ? null : response.data)
  }
}

const mutations = {
  SET_CAMPAIGN_MARKET_POSITIONS(state, { rows, windowHours, truncated, limit }) {
    // Replace the whole map so reactivity works without Vue.set (kept Vue 3 compatible)
    state.campaignMarketPositions = rows.reduce((map, position) => {
      map[position.campaignId] = position
      return map
    }, {})

    // Committed even when rows is empty - an empty response is exactly the state the window
    // label exists to explain ("no bids in the last 24 hours" reads very differently from
    // "no bids in the last 7 days")
    state.campaignMarketPositionsWindowHours = windowHours ?? null

    state.campaignMarketPositionsTruncated = truncated === true
    state.campaignMarketPositionsLimit = limit ?? null
  },

  SET_CATEGORY_MARKET_CONTEXT(state, context) {
    state.categoryMarketContext = context
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
